import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Link,
  withRouter,
} from 'react-router-dom';
import classnames from 'classnames';
import {
  Button,
  Input,
  Form,
  Nav,
  NavItem,
  NavLink,
  Popover,
  PopoverBody,
} from 'reactstrap';
import Parser from 'html-react-parser';

import {
  addSelectedCollectionFilter,
  clearAllCollectionFilters,
  setCollectionFilters,
  setCollectionsParams,
  clearCollectionsList,
  fetchCollections,
} from '../../actions/Collections';
import {
  addSelectedFilter,
  deleteSelectedFilter,
  setFilesParams,
  fetchFiles,
  clearFilesFilters,
  clearSelectedFilters,
  clearFilesList,
} from '../../actions/Files';
import { setMiniOverlay, setSidebarOpen } from '../../actions/Mini';
import { setSearchTerm } from '../../actions/Search';
import { fetchTaxonomies, fetchMetadataGroups, fetchCustomFields } from '../../actions/Metadata';

import CollectionsBreadcrumbs from '../../components/Collections/Breadcrumbs';
import MiniHeaderButtons from './Header/Buttons';
import MiniHeaderProfile from './Header/Profile';
import AdobeSave from './AdobeSave';
import FigmaSave from './Figma/FigmaSave';
import FigmaResize from './Figma/FigmaResize';

import {
  isMiniAdobeIntegration,
  isMiniFigmaIntegration,
} from '../../helpers/App';
import {
  isAllCollectionsPage,
  isCollectionsPage,
} from '../../helpers/Collections';
import { isAllFilesPage } from '../../helpers/Files';
import { addFlashMessage } from '../../helpers/FlashMessages';

const MiniHeader = ({
  miniConfig,
  history,
  collection,
  sidebarOpen,
  switchSidebarOpen,
  removeSearch,
  setSearch,
  loadMetadataGroups,
  loadTaxonomies,
  loadCustomFields,
  handleMiniOverlay,
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchField, setSearchField] = useState('');

  useEffect(() => {
    loadMetadataGroups();
    loadTaxonomies();
    loadCustomFields();
  });

  const handleMessage = useCallback(async (event) => {
    if (!miniConfig) return;

    if (miniConfig.integrationId === 'shopify') {
      const { name } = event.data;

      if (name === 'handle-overlay') {
        handleMiniOverlay(event.data.data.overlay);
      }

      if (name === 'file-upload-success') {
        addFlashMessage({
          type: 'success',
          text: Parser('<strong>File uploaded successfully.</strong><br />You can now view it in your Shopify file library.'),
        });
      }

      if (name === 'file-upload-success-product') {
        addFlashMessage({
          type: 'success',
          text: Parser('<strong>File uploaded successfully.</strong><br />It was assigned to the selected product.'),
        });
      }

      if (name === 'files-upload-success') {
        addFlashMessage({
          type: 'success',
          text: Parser('<strong>Files uploaded successfully.</strong><br />You can now view them in your Shopify file library.'),
        });
      }

      if (name === 'files-upload-success-product') {
        addFlashMessage({
          type: 'success',
          text: Parser('<strong>Files uploaded successfully.</strong><br />They were assigned to the selected product.'),
        });
      }

      if (name === 'file-upload-error') {
        addFlashMessage({
          type: 'danger',
          text: Parser('<strong>Something went wrong during the upload.</strong><br />Please try again or contact us.'),
        });
      }
    }

    if (miniConfig.integrationId.startsWith('adobe')) {
      const { name } = event.data;

      if (name === 'handle-overlay') {
        handleMiniOverlay(event.data.data.overlay);
      }
    }
  }, [handleMiniOverlay, miniConfig]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    window.parent.postMessage(
      {
        source: 'spmini',
        event: 'stockpress-message-handler-ready',
      },
      '*',
    );

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  const handleChange = (e) => {
    const { value } = e.target;
    setSearchField(value);
  };

  const handleSearch = (e) => {
    e.preventDefault();

    removeSearch();
    setSearch({
      type: 'search',
      key: `${searchField}`,
      label: `Search: ${searchField}`,
    }, true);
    setSearchField('');
    setIsSearchOpen(false);
    history.push('/results');
  };

  return (
    <>
      <div className="header-space" />
      <header className="page-header">
        <div className="navigation-wrapper">
          <div className="menu-wrapper">
            <Button
              type="button"
              className="navbar-toggler mini-sidebar-toggler"
              onClick={() => {
                switchSidebarOpen(!sidebarOpen);
              }}
              aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
            >
              {sidebarOpen ? (
                <i className="fal fa-xmark" />
              ) : (
                <i className="fal fa-bars" />
              )}
            </Button>
            <Nav className="header-navigation">
              <NavItem>
                <NavLink tag={Link} to="/files" className={classnames({ active: isAllFilesPage() })}>
                  <i className="fal fa-file" />
                  <span className="nav-link-title">
                    All Files
                  </span>
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink tag={Link} to="/collections" className={classnames({ active: isCollectionsPage() })}>
                  <i className="fal fa-layer-group" />
                  <span className="nav-link-title">
                    All Collections
                  </span>
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink id="mini-search" tag="a" href="#search" className={classnames({ active: isSearchOpen })}>
                  <i className="fal fa-search" />
                  <span className="nav-link-title">
                    Search
                  </span>
                </NavLink>
                <Popover
                  className="mini-search-popover"
                  target="mini-search"
                  placement="bottom"
                  fade={false}
                  hideArrow={true}
                  isOpen={isSearchOpen}
                  toggle={() => { setIsSearchOpen(!isSearchOpen); }}
                >
                  <PopoverBody>
                    <Form onSubmit={handleSearch}>
                      <Input
                        type="text"
                        name="mini-search"
                        placeholder="Search file name, keyword or tag"
                        autoFocus={true}
                        onChange={handleChange}
                        value={searchField}
                      />
                    </Form>
                  </PopoverBody>
                </Popover>
              </NavItem>
              <FigmaResize />
              <MiniHeaderButtons />
            </Nav>
          </div>
          <div className="right-wrapper">
            {isMiniAdobeIntegration() && <AdobeSave /> }
            {isMiniFigmaIntegration() && <FigmaSave /> }
            <MiniHeaderProfile />
          </div>
        </div>
      </header>
      <div className="subheader">
        {collection && (
          <CollectionsBreadcrumbs collection={collection} />
        )}
        <div className="page-header-title">
          {collection ? (
            <h5 className={`${collection && collection.archived ? 'archived ' : ''}page-title`}>
              {collection.name}
            </h5>
          ) : (
            (isCollectionsPage() || isAllCollectionsPage()) && (
              <h5 className="page-title">
                All Collections
              </h5>
            )
          )}
          {isAllFilesPage() && (
            <h5 className="page-title">
              All Files
            </h5>
          )}
        </div>
      </div>
    </>
  );
};

const mapStateToProps = state => ({
  collection: state.Collections.collection,
  sidebarOpen: state.Mini.sidebarOpen,
  miniConfig: state.Mini.config,
});

const mapDispatchToProps = dispatch => ({
  setSearch: (filter, refresh = true) => {
    dispatch(clearFilesFilters());
    dispatch(clearSelectedFilters());
    dispatch(clearCollectionsList());
    dispatch(setSearchTerm(filter.key));
    dispatch(addSelectedFilter(filter));
    dispatch(setFilesParams({ orderby: 'relevance' }));
    if (refresh) {
      dispatch(fetchFiles());
      dispatch(fetchCollections());
    }
  },
  removeSearch: () => {
    dispatch(setSearchTerm(''));
    dispatch(deleteSelectedFilter('search'));
  },
  setCollectionsSearch: (filter) => {
    dispatch(clearAllCollectionFilters());
    dispatch(clearFilesList());
    dispatch(setCollectionFilters({ search: filter.key }));
    dispatch(addSelectedCollectionFilter(filter));
    dispatch(setCollectionsParams({ orderby: 'relevance' }));
  },
  switchSidebarOpen: (open) => {
    dispatch(setSidebarOpen(open));
  },
  loadMetadataGroups: () => {
    dispatch(fetchMetadataGroups());
  },
  loadTaxonomies: () => {
    dispatch(fetchTaxonomies());
  },
  loadCustomFields: () => {
    dispatch(fetchCustomFields());
  },
  handleMiniOverlay: (overlay) => (
    dispatch(setMiniOverlay(overlay))
  ),
});

MiniHeader.propTypes = {
  miniConfig: PropTypes.object,
  history: PropTypes.object.isRequired,
  setSearch: PropTypes.func.isRequired,
  removeSearch: PropTypes.func.isRequired,
  switchSidebarOpen: PropTypes.func.isRequired,
  collection: PropTypes.object,
  sidebarOpen: PropTypes.bool.isRequired,
  loadMetadataGroups: PropTypes.func.isRequired,
  loadTaxonomies: PropTypes.func.isRequired,
  loadCustomFields: PropTypes.func.isRequired,
  handleMiniOverlay: PropTypes.func.isRequired,
};

MiniHeader.defaultProps = {
  miniConfig: null,
  collection: null,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MiniHeader));
